import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AlertCircle,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  History
} from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import OptimizationForm from './OptimizationForm';
import OptimizationResults from './OptimizationResults';
import OptimizationEditModal from './OptimizationEditModal';
import OptimizationService from '../services/optimizationService';
import {
  OptimizationRun,
  OptimizationResults as OptimizationResultsType,
  OptimizedContainer,
  OptimizedOrder
} from '../types/optimization';

type ViewMode = 'configure' | 'results' | 'history';

const OptimizationDashboard: React.FC = () => {
  const { activeDatabase } = useDatabaseContext();
  const [viewMode, setViewMode] = useState<ViewMode>('configure');
  const [isRunning, setIsRunning] = useState(false);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [results, setResults] = useState<OptimizationResultsType | null>(null);
  const [optimizationRuns, setOptimizationRuns] = useState<OptimizationRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<OptimizedContainer | undefined>();
  const [editingOrder, setEditingOrder] = useState<{ order: OptimizedOrder; containerId: string } | undefined>();

  const loadOptimizationHistory = useCallback(async () => {
    if (!activeDatabase) return;

    try {
      const runs = await OptimizationService.listOptimizationRuns(activeDatabase.id);
      setOptimizationRuns(runs);
    } catch (error) {
      console.error('Error loading optimization history:', error);
    }
  }, [activeDatabase]);

  useEffect(() => {
    if (activeDatabase) {
      loadOptimizationHistory();
    }
  }, [activeDatabase, loadOptimizationHistory]);

  useEffect(() => {
    // Clean up polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array to only run on mount/unmount

  const handleOptimizationStart = (runId: string) => {
    // Prevent starting multiple polling intervals
    if (isRunning && pollingIntervalRef.current) {
      console.log('Optimization already running, ignoring start request');
      return;
    }
    
    setCurrentRunId(runId);
    setIsRunning(true);
    setError(null);
    setSuccess(null);
    
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      console.log('Clearing existing polling interval');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Start polling for updates
    console.log(`Starting polling for optimization run ${runId}`);
    const interval = setInterval(() => {
      checkOptimizationStatus(runId);
    }, 2000);
    
    pollingIntervalRef.current = interval;
  };

  const checkOptimizationStatus = async (runId: string) => {
    if (!activeDatabase) return;

    try {
      console.log(`Checking optimization status for run ${runId}`);
      const runs = await OptimizationService.listOptimizationRuns(activeDatabase.id);
      const currentRun = runs.find(run => run.id === runId);
      
      if (currentRun && currentRun.status !== 'running') {
        console.log(`Optimization ${runId} completed with status: ${currentRun.status}`);
        setIsRunning(false);
        
        // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        if (currentRun.status === 'completed') {
          await loadOptimizationResults(runId);
          setSuccess('Optimisation terminée avec succès !');
          setViewMode('results');
        } else if (currentRun.status === 'failed') {
          setError(currentRun.error_message || 'L\'optimisation a échoué');
        }
        
        loadOptimizationHistory();
      }
    } catch (error) {
      console.error('Error checking optimization status:', error);
      setIsRunning(false);
      
      // Clear polling interval on error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
    }
  };

  const handleOptimizationComplete = async (result: any) => {
    setIsRunning(false);
    
    // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
    
    await loadOptimizationResults(result.optimization_run_id);
    setSuccess('Optimisation terminée avec succès !');
    setViewMode('results');
    loadOptimizationHistory();
  };

  const loadOptimizationResults = async (runId: string) => {
    if (!activeDatabase) return;

    try {
      const results = await OptimizationService.getOptimizationResults(
        activeDatabase.id,
        runId
      );
      setResults(results);
      setCurrentRunId(runId);
    } catch (error) {
      console.error('Error loading optimization results:', error);
      setError('Erreur lors du chargement des résultats');
    }
  };

  const handleViewHistoryResults = async (run: OptimizationRun) => {
    await loadOptimizationResults(run.id);
    setViewMode('results');
  };

  const handleContainerEdit = (container: OptimizedContainer) => {
    setEditingContainer(container);
    setEditingOrder(undefined);
    setIsEditModalOpen(true);
  };

  const handleContainerDelete = async (containerId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce container ?')) {
      return;
    }
    
    // TODO: Implement container deletion API call
    setSuccess('Container supprimé avec succès');
    
    // Refresh results
    if (currentRunId && activeDatabase) {
      await loadOptimizationResults(currentRunId);
    }
  };

  const handleOrderEdit = (order: OptimizedOrder, containerId: string) => {
    setEditingOrder({ order, containerId });
    setEditingContainer(undefined);
    setIsEditModalOpen(true);
  };

  const handleModalSave = async (updateData: any) => {
    try {
      // TODO: Implement backend API calls for updates
      console.log('Saving update:', updateData);
      
      setSuccess(
        updateData.type === 'container' 
          ? 'Container mis à jour avec succès'
          : 'Commande mise à jour avec succès'
      );
      
      // Refresh results
      if (currentRunId && activeDatabase) {
        await loadOptimizationResults(currentRunId);
      }
    } catch (error) {
      console.error('Error saving update:', error);
      setError('Erreur lors de la sauvegarde');
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setSuccess(null);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-600" size={16} />;
      case 'failed':
        return <XCircle className="text-red-600" size={16} />;
      case 'running':
        return <RefreshCw className="text-blue-600 animate-spin" size={16} />;
      default:
        return <Clock className="text-yellow-600" size={16} />;
    }
  };

  if (!activeDatabase) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Aucune base de données sélectionnée
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Sélectionnez une base de données pour utiliser l'optimisation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">
          Optimisation des Approvisionnements
        </h1>
        
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => { setViewMode('configure'); clearMessages(); }}
            className={`px-4 py-2 rounded-md transition-colors ${
              viewMode === 'configure'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => { setViewMode('results'); clearMessages(); }}
            disabled={!results}
            className={`px-4 py-2 rounded-md transition-colors ${
              viewMode === 'results' && results
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            Résultats
          </button>
          <button
            onClick={() => { setViewMode('history'); clearMessages(); }}
            className={`px-4 py-2 rounded-md transition-colors ${
              viewMode === 'history'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <History className="inline w-4 h-4 mr-1" />
            Historique
          </button>
        </div>

        <p className="text-gray-600">
          Base de données active : <span className="font-medium">{activeDatabase.name}</span>
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="text-red-600" size={16} />
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <XCircle size={16} />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={16} />
            <p className="text-green-700">{success}</p>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <XCircle size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'configure' && (
        <OptimizationForm
          onOptimizationStart={handleOptimizationStart}
          onOptimizationComplete={handleOptimizationComplete}
          onError={handleError}
          isRunning={isRunning}
        />
      )}

      {viewMode === 'results' && results && (
        <OptimizationResults
          results={results}
          onContainerEdit={handleContainerEdit}
          onContainerDelete={handleContainerDelete}
          onOrderEdit={handleOrderEdit}
        />
      )}

      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Historique des optimisations
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {optimizationRuns.length === 0 ? (
              <div className="p-12 text-center">
                <History className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Aucune optimisation
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Lancez votre première optimisation pour voir l'historique.
                </p>
              </div>
            ) : (
              optimizationRuns.map((run) => (
                <div key={run.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(run.status)}
                      <div>
                        <p className="font-medium text-gray-900">
                          Optimisation #{run.id}
                        </p>
                        <p className="text-sm text-gray-600">
                          Date cible : {new Date(run.target_month).toLocaleDateString('fr-FR')}
                          {run.model_name && ` • Modèle : ${run.model_name}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(run.created_at).toLocaleDateString('fr-FR')} à {' '}
                          {new Date(run.created_at).toLocaleTimeString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {run.status === 'completed' && (
                        <div className="text-sm text-gray-600">
                          <p>{run.total_containers} containers</p>
                          <p>{run.total_volume_m3?.toFixed(2)} m³</p>
                        </div>
                      )}
                      
                      {run.status === 'completed' && (
                        <button
                          onClick={() => handleViewHistoryResults(run)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md"
                        >
                          Voir résultats
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {run.error_message && (
                    <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                      <p className="text-sm text-red-700">{run.error_message}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <OptimizationEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingContainer(undefined);
          setEditingOrder(undefined);
        }}
        container={editingContainer}
        orderToEdit={editingOrder}
        onSave={handleModalSave}
      />
    </div>
  );
};

export default OptimizationDashboard;
